export const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://127.0.0.1:8000';

// Outage → full-page ErrorPage (App.js listens). Only for GETs: a failed GET means
// the page can't load, while a failed save stays inline so the user keeps what
// they typed.
export function apiErrorCode(status, header) {
  if (header === 'db_unavailable') return 'db_down';
  if (status === 500) return 'server';
  if (status >= 502 && status <= 504) return 'backend_down';
  return null;
}
const reportApiError = (code) => window.dispatchEvent(new CustomEvent('api:error', { detail: code }));

// Global fetch interceptor: 401 → logout, outages → error page
const originalFetch = window.fetch;
window.fetch = async (...args) => {
  const url = typeof args[0] === 'string' ? args[0] : (args[0] && args[0].url ? args[0].url : '');
  const method = (args[1]?.method || args[0]?.method || 'GET').toUpperCase();
  const watch = url.startsWith(API_BASE_URL) && method === 'GET';

  let response;
  try {
    response = await originalFetch(...args);
  } catch (err) {
    if (watch && err.name !== 'AbortError') reportApiError(navigator.onLine === false ? 'offline' : 'backend_down');
    throw err;
  }
  if (watch) {
    const code = apiErrorCode(response.status, response.headers?.get?.('X-Michii-Error'));
    if (code) reportApiError(code);
  }
  if (response.status === 401) {
    // Ignore login/register endpoints that might legitimately return 401 for invalid credentials
    if (!url.includes('/api/auth/token') && !url.includes('/api/auth/register')) {
      window.dispatchEvent(new Event('auth:unauthorized'));
    }
  }
  return response;
};

// Where to land after auth. Stashed in localStorage rather than a ?redirect= param
// because the Google round trip (and the emailed verification link) drop query
// params on the way back.
export const POST_LOGIN_REDIRECT_KEY = 'michii_post_login_redirect';
export const PENDING_SHARE_KEY = 'michii_pending_share';

// An explicit ?redirect= wins. Otherwise resume a share link the user opened
// before logging in: they can reach /login from the navbar, a bookmark or the
// Google round trip, not just from the share page's own button, and every one of
// those paths used to dump them on the home page with no idea what they clicked.
export function postLoginDestination(explicit) {
  if (explicit) return explicit;
  const shareToken = localStorage.getItem(PENDING_SHARE_KEY);
  return shareToken ? `/share?token=${shareToken}` : '/';
}

export const CHROME_STORE_URL = "https://chromewebstore.google.com/detail/pmdanmglncjdgnonocgollpnenhjnlli?utm_source=item-share-cb"; 

export const endpoints = {
  auth: {
    register:           `${API_BASE_URL}/api/auth/register`,
    token:              `${API_BASE_URL}/api/auth/token`,
    google:             `${API_BASE_URL}/api/auth/google`,
    verifyEmail:        `${API_BASE_URL}/api/auth/verify-email`,
    resendVerification: `${API_BASE_URL}/api/auth/resend-verification`,
    forgotPassword:     `${API_BASE_URL}/api/auth/forgot-password`,
    resetPassword:      `${API_BASE_URL}/api/auth/reset-password`,
    me:                 `${API_BASE_URL}/api/auth/me`,
  },
  // ── New payment endpoints (Razorpay + Stripe) ──────────────────────────────
  payments: {
    plans:          `${API_BASE_URL}/api/payments/plans`,
    checkoutSession:`${API_BASE_URL}/api/payments/checkout/session`,
    status:         `${API_BASE_URL}/api/payments/status`,
    cancel:         `${API_BASE_URL}/api/payments/cancel`,
    billingPortal:  `${API_BASE_URL}/api/payments/billing-portal`,
  },
  workspace: {
    base:         `${API_BASE_URL}/api/workspace`,
    invite:       `${API_BASE_URL}/api/workspace/invite`,
    members:      `${API_BASE_URL}/api/workspace/members`,
    removeMember: (userId)  => `${API_BASE_URL}/api/workspace/members/${userId}`,
    transferGuide:(guideId) => `${API_BASE_URL}/api/workspace/guides/${guideId}/transfer`,
  },
  guides: {
    base: `${API_BASE_URL}/api/guides/`,
    public: `${API_BASE_URL}/api/guides/public`,
    search: `${API_BASE_URL}/api/guides/search`,
    detail: (id) => `${API_BASE_URL}/api/guides/${id}`,
    update: (id) => `${API_BASE_URL}/api/guides/${id}`,
    steps: (id) => `${API_BASE_URL}/api/guides/${id}/steps`,
    play: (id) => `${API_BASE_URL}/api/guides/${id}/play`,
    exportPdf: (id) => `${API_BASE_URL}/api/guides/${id}/export-pdf`,
    exportVideo: (id) => `${API_BASE_URL}/api/guides/${id}/export-video`,
    shareToken: (id) => `${API_BASE_URL}/api/guides/${id}/share-token`,
    claimAccess: (token) => `${API_BASE_URL}/api/guides/share/access/${token}`,
    sharePreview: (token) => `${API_BASE_URL}/api/guides/share/preview/${token}`,
  },
  feedback: {
    uninstall: `${API_BASE_URL}/api/feedback/uninstall`,
  },
  analytics: {
    guide: (id) => `${API_BASE_URL}/api/analytics/guide/${id}`,
  },
};
